// pms-core / domain — THE DIRECT QUOTE (A4, RESERVATION-API-BRIEF §7).
//
// Pure: the facts `pms_public_offer` returns go in, a sellability verdict or a
// priced stay comes out. Nothing here reads a database or a clock.
//
// The arithmetic is NOT re-implemented: `tierFor` and `mealComponent` are the
// grid generator's own (`rateGenerator.ts`), so the site's price and the grid
// move together whenever a board or a rung is priced differently.
//
//   direct night = room(date) × (1 − discountStay) + Σ persons meal(board, band) × (1 − discountMeals)
//     room(date) = the ROOT `STD` grid row for the category and date (hand-adjusted included)
//                  — never depends on occupancy (PMS-PHONE-BOOKING-BRIEF P9)
//     meal       = the profile's DIRECT prices (§11.6b), never the OTA pair inside BB/DP rows:
//                  an adult, a 13–17 or a child of unknown age at `mealsDirect` (never
//                  undersell); a 3–5, 6–8 or 9–12 at `childMealsDirect[band]` (P7, amending
//                  board brief Q6 for the direct price); infants are never counted (D1)
//   taxe de séjour = tariff × adults × nights, a SEPARATE line (O20)
//
// A night with no row, a closed row, a stay shorter than the strictest min stay
// of its nights or a hotel closure makes the category unsellable — nothing is
// priced by fallback.

import type { AgeBand, Cents, IsoDate, RatePlan } from './types.js';
import { CHILD_MEAL_BANDS, type ChildMealPrices, type ChildMealBand, type LengthOfStayTier, type MealPrices } from './rateParameters.js';
import { mealComponent, tierFor } from './rateGenerator.js';
import { categoryDominates, type RoomFit } from './roomFit.js';
import type { TermsRules } from './cancellationPolicy.js';

// ── The facts (the RPC's jsonb, typed) ───────────────────────────────────────

export interface OfferRateRow {
  priceCents: Cents;
  closed: boolean;
  minStay: number;
  /** CC2 — absent on a database without CC1/CC2: nothing restricted. */
  closedToArrival?: boolean;
  closedToDeparture?: boolean;
  maxStay?: number | null;
}

export interface OfferNight {
  date: IsoDate;
  available: number;
  /** Root-plan code → that plan's grid row for this night. Absent = no row. */
  rates: Record<string, OfferRateRow>;
}

export interface OfferCategory {
  id: number;
  code: string;
  maxOccupancy: number;
  nights: OfferNight[];
  /** CC2 — root-plan code → closed to departure on the DEPARTURE DAY, which is
   *  not a night of the stay. Absent on a database without CC2. */
  departure?: Record<string, { closedToDeparture: boolean }>;
}

export interface OfferPlan {
  id: number;
  code: string;
  board: RatePlan['board'];
  /** The plan's cancellation terms (PB7b) — what the site shows and the stay
   *  copies. null = the plan follows no policy; absent before PB7b. */
  terms?: TermsRules | null;
}

export interface OfferExtra {
  code: string;
  priceCents: Cents;
  perPerson: boolean;
}

export interface PublicOfferFacts {
  propertyCode: string;
  from: IsoDate;
  /** Exclusive: the departure date. */
  to: IsoDate;
  /** The most recently PUBLISHED profile's ladder and direct meals, or null.
   *  `childMealsDirect` is null (or absent) for a profile published before PB1. */
  profile: {
    lengthOfStayTiers: LengthOfStayTier[];
    mealsDirect: MealPrices;
    childMealsDirect?: ChildMealPrices | null;
  } | null;
  taxPerPersonNightCents: Cents | null;
  plans: OfferPlan[];
  hotelClosedNights: IsoDate[];
  categories: OfferCategory[];
  extras: OfferExtra[];
}

export interface StayParty {
  adults: number;
  children: number;
  infants: number;
  /** Each child's age band, in any order (P5). Fewer entries than `children`
   *  leaves the rest of unknown age — priced as adults, never undersold. The
   *  website sends none until PB6. */
  childBands?: readonly AgeBand[];
}

/** One night's meals for the party on this board, before the meal discount. */
export function directMeals(
  board: RatePlan['board'],
  party: StayParty,
  mealsDirect: MealPrices,
  childMealsDirect: ChildMealPrices | null | undefined,
): Cents {
  const priced = (meals: MealPrices) => mealComponent(board, 1, meals);
  let total = party.adults * priced(mealsDirect);
  for (let i = 0; i < party.children; i++) {
    const band = party.childBands?.[i];
    const child =
      childMealsDirect != null && band !== undefined && (CHILD_MEAL_BANDS as readonly string[]).includes(band)
        ? childMealsDirect[band as ChildMealBand]
        : mealsDirect;
    total += priced(child);
  }
  return total;
}

// ── Sellability ──────────────────────────────────────────────────────────────

export type UnsellableReason =
  | 'not_on_sale' // no published profile for the établissement
  | 'too_many_guests'
  | 'hotel_closed'
  | 'no_rate'
  | 'closed'
  | 'min_stay'
  // CC2 (operator Q3: the direct site enforces what the OTAs are sent)
  | 'closed_to_arrival'
  | 'closed_to_departure'
  | 'max_stay'
  | 'full';

/** How many are left, as the site may say it (Q3): an exact count up to 3,
 *  « more » above — the inventory itself is commercial information. */
export type RoomsLeft = 1 | 2 | 3 | 'more';

export type CategoryAvailability =
  | { category: string; plan: string; sellable: true; left: RoomsLeft }
  | { category: string; plan: string; sellable: false; reason: UnsellableReason; minStay?: number; maxStay?: number };

/** Every (category × root plan) for the stay, sellable or with its reason. */
export function offerAvailability(facts: PublicOfferFacts, party: StayParty): CategoryAvailability[] {
  const out: CategoryAvailability[] = [];
  for (const cat of facts.categories) {
    for (const plan of facts.plans) out.push(judge(facts, cat, plan, party));
  }
  return out;
}

function judge(
  facts: PublicOfferFacts,
  cat: OfferCategory,
  plan: OfferPlan,
  party: StayParty,
): CategoryAvailability {
  const no = (reason: UnsellableReason, minStay?: number): CategoryAvailability =>
    minStay === undefined
      ? { category: cat.code, plan: plan.code, sellable: false, reason }
      : { category: cat.code, plan: plan.code, sellable: false, reason, minStay };

  // Ordered from the reason most useful to the guest to the least: a full
  // hotel matters less than « this room cannot take four of you ».
  if (facts.profile === null) return no('not_on_sale');
  if (party.adults + party.children > cat.maxOccupancy) return no('too_many_guests');
  if (cat.nights.some((n) => facts.hotelClosedNights.includes(n.date))) return no('hotel_closed');

  const nights = cat.nights.length;
  let minStay = 1;
  let left = Number.POSITIVE_INFINITY;
  for (const n of cat.nights) {
    const row = n.rates[plan.code];
    // The room component is always the STD row (§7.3); a BB/DP night needs it too.
    if (row === undefined || n.rates['STD'] === undefined) return no('no_rate');
    if (row.closed) return no('closed');
    minStay = Math.max(minStay, row.minStay);
    left = Math.min(left, n.available);
  }
  if (nights === 0) return no('no_rate');
  if (nights < minStay) return no('min_stay', minStay);
  // CC2 — the arrival night's row says whether a stay may START here and how
  // long it may be; the departure day's says whether it may END there.
  const arrival = cat.nights[0]!.rates[plan.code]!;
  if (arrival.closedToArrival === true) return no('closed_to_arrival');
  if (cat.departure?.[plan.code]?.closedToDeparture === true) return no('closed_to_departure');
  const maxStay = arrival.maxStay ?? null;
  if (maxStay !== null && nights > maxStay) {
    return { category: cat.code, plan: plan.code, sellable: false, reason: 'max_stay', maxStay };
  }
  if (left <= 0) return no('full');
  return { category: cat.code, plan: plan.code, sellable: true, left: left > 3 ? 'more' : (left as 1 | 2 | 3) };
}

// ── CC2 — the stay restrictions, for a door that WARNS instead of refusing ──
//
// The site refuses a stay that breaks closed to arrival / departure or max stay
// (`judge` above). The desk does not (operator, Q3): it sees a warning and may
// sell anyway — a guest on the phone is a decision, not a rule. So the desk
// judges the facts with these three lifted, and asks this for the warnings.

export type StayRestriction = 'closed_to_arrival' | 'closed_to_departure' | 'max_stay';

/** Which of the three a stay of this category × root plan breaks. */
export function stayRestrictionsBroken(
  cat: OfferCategory,
  planCode: string,
): { broken: StayRestriction[]; maxStay: number | null } {
  const arrival = cat.nights[0]?.rates[planCode];
  const broken: StayRestriction[] = [];
  if (arrival?.closedToArrival === true) broken.push('closed_to_arrival');
  if (cat.departure?.[planCode]?.closedToDeparture === true) broken.push('closed_to_departure');
  const maxStay = arrival?.maxStay ?? null;
  if (maxStay !== null && cat.nights.length > maxStay) broken.push('max_stay');
  return { broken, maxStay };
}

/** The same facts with the three restrictions lifted — what the desk prices. */
export function withoutStayRestrictions<F extends PublicOfferFacts>(facts: F): F {
  return {
    ...facts,
    categories: facts.categories.map((c) => ({
      ...c,
      departure: undefined,
      nights: c.nights.map((n) => ({
        ...n,
        rates: Object.fromEntries(
          Object.entries(n.rates).map(([code, r]) => [
            code,
            { ...r, closedToArrival: false, closedToDeparture: false, maxStay: null },
          ]),
        ),
      })),
    })),
  };
}

// ── The price ────────────────────────────────────────────────────────────────

export interface DirectQuote {
  propertyCode: string;
  category: string;
  plan: string;
  from: IsoDate;
  to: IsoDate;
  party: StayParty;
  nights: { date: IsoDate; priceCents: Cents }[];
  totalCents: Cents;
  /** The rung applied, or null when the ladder has none for this length. */
  tier: { minNights: number; discountStay: number; discountMeals: number } | null;
  /** Shown separately, never inside `totalCents` (O20). */
  taxCents: Cents;
  extras: OfferExtra[];
}

export type QuoteOutcome =
  | { ok: true; quote: DirectQuote }
  | { ok: false; reason: UnsellableReason | 'unknown_category' | 'unknown_plan'; minStay?: number; maxStay?: number };

/** `lodged`: the sold category is full and `sellAs` found it a host (P6). The
 *  stay is still priced on the SOLD category's own rows, so only its free room
 *  is not asked — every other reason (rate, closure, min stay, size) still
 *  refuses. Never set it without a `sellAs` that answered `lodged: true`. */
export function quoteStay(
  facts: PublicOfferFacts,
  categoryCode: string,
  planCode: string,
  party: StayParty,
  opts: { lodged?: boolean } = {},
): QuoteOutcome {
  const cat = facts.categories.find((c) => c.code === categoryCode);
  if (cat === undefined) return { ok: false, reason: 'unknown_category' };
  const plan = facts.plans.find((p) => p.code === planCode);
  if (plan === undefined) return { ok: false, reason: 'unknown_plan' };

  const verdict = judge(facts, cat, plan, party);
  // judge() answers `full` only once every other check has passed.
  if (!verdict.sellable && !(opts.lodged === true && verdict.reason === 'full')) {
    return {
      ok: false,
      reason: verdict.reason,
      ...(verdict.minStay === undefined ? {} : { minStay: verdict.minStay }),
      ...(verdict.maxStay === undefined ? {} : { maxStay: verdict.maxStay }),
    };
  }
  // Sellable (or full and lodged), so the profile is present and every night
  // has its STD row; the checks below only narrow the types.
  const profile = facts.profile;
  if (profile === null) return { ok: false, reason: 'not_on_sale' };

  const count = cat.nights.length;
  const tier = tierFor(profile, count);
  const stayOff = tier?.discountStay ?? 0;
  const mealsOff = tier?.discountMeals ?? 0;
  const meals = directMeals(plan.board, party, profile.mealsDirect, profile.childMealsDirect);

  const nights: DirectQuote['nights'] = [];
  for (const n of cat.nights) {
    const room = n.rates['STD'];
    if (room === undefined) return { ok: false, reason: 'no_rate' };
    // Rounded once per night, never per component (directNightlyRate's rule).
    nights.push({ date: n.date, priceCents: Math.round(room.priceCents * (1 - stayOff) + meals * (1 - mealsOff)) });
  }

  return {
    ok: true,
    quote: {
      propertyCode: facts.propertyCode,
      category: cat.code,
      plan: plan.code,
      from: facts.from,
      to: facts.to,
      party,
      nights,
      totalCents: nights.reduce((s, n) => s + n.priceCents, 0),
      tier: tier === null ? null : { minNights: tier.minNights, discountStay: stayOff, discountMeals: mealsOff },
      taxCents: (facts.taxPerPersonNightCents ?? 0) * party.adults * count,
      extras: facts.extras,
    },
  };
}

// ── P6 — sold in its category, lodged bigger ─────────────────────────────────
//
// PMS-PHONE-BOOKING-BRIEF §3. When the category asked for has no room free on
// every night, it is still sold — at its own price, under its own category and
// plan — and lodged in the first category that dominates it (`categoryDominates`,
// TETRIS §6.3), is BIGGER, and is free on every night. « We sell a triple as a double, no
// penalty. » The stay then records `soldRoomTypeId` = the sold category and
// `roomTypeId` = the host, because availability and the ARI push count a stay
// against its own `roomTypeId`: the host comes off sale, the sold category does
// not go negative.
//
// The hosts are tried same standing first, smallest first, a superior room last
// (flagged `upgrade` — « surclassement », Q4): for a DSTD that is TSTD · FAM ·
// TSUP · FAMS; for a TSTD, FAM · FAMS. Never for apartments (Q7): each unit is its own product, so the
// caller passes `lodgeBigger: false` there.

export type SaleOutcome =
  | {
      ok: true;
      /** The category bought and priced — always the one asked for. */
      sold: string;
      /** The category the stay occupies: `sold` itself unless it was full. */
      host: string;
      /** A host other than the sold category was needed. */
      lodged: boolean;
      /** The host is superior and the sold category is not (Q4). */
      upgrade: boolean;
      left: RoomsLeft;
    }
  | { ok: false; reason: UnsellableReason | 'unknown_category' | 'unknown_plan'; minStay?: number; maxStay?: number };

export function sellAs(
  facts: PublicOfferFacts,
  fit: RoomFit,
  soldCode: string,
  planCode: string,
  party: StayParty,
  opts: { lodgeBigger: boolean },
): SaleOutcome {
  const sold = facts.categories.find((c) => c.code === soldCode);
  if (sold === undefined) return { ok: false, reason: 'unknown_category' };
  const plan = facts.plans.find((p) => p.code === planCode);
  if (plan === undefined) return { ok: false, reason: 'unknown_plan' };

  // Everything but the free room is the SOLD category's to answer: its rows
  // price the stay, its min stay applies, its size must take the party.
  const verdict = judge(facts, sold, plan, party);
  if (verdict.sellable) {
    return { ok: true, sold: sold.code, host: sold.code, lodged: false, upgrade: false, left: verdict.left };
  }
  if (verdict.reason !== 'full' || !opts.lodgeBigger) {
    return {
      ok: false,
      reason: verdict.reason,
      ...(verdict.minStay === undefined ? {} : { minStay: verdict.minStay }),
      ...(verdict.maxStay === undefined ? {} : { maxStay: verdict.maxStay }),
    };
  }

  // BIGGER, not merely dominating (operator, 2026-10-26): a DSUP dominates a
  // DSTD and a TSUP a TSTD, but handing either over is a surclassement — the
  // day-before gift of TETRIS §6 — not a sale lodged where it fits.
  const profile = (c: OfferCategory) => fit.categories.get(c.id);
  const soldSize = profile(sold)?.size ?? Number.POSITIVE_INFINITY;
  const hosts = facts.categories
    .filter((c) => categoryDominates(fit, c.id, sold.id) && profile(c)!.size > soldSize)
    .filter((c) => c.nights.length === sold.nights.length && c.nights.every((n) => n.available > 0))
    .sort((a, b) => {
      const pa = profile(a)!;
      const pb = profile(b)!;
      return Number(pa.superior) - Number(pb.superior) || pa.size - pb.size || a.code.localeCompare(b.code);
    });
  const host = hosts[0];
  if (host === undefined) return { ok: false, reason: 'full' };

  const left = Math.min(...host.nights.map((n) => n.available));
  return {
    ok: true,
    sold: sold.code,
    host: host.code,
    lodged: true,
    upgrade: profile(host)!.superior && !profile(sold)!.superior,
    left: left > 3 ? 'more' : (left as 1 | 2 | 3),
  };
}
